import { ActionButton, Buttons, CancelButton, Icon, Level, ModalCard } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '~/styles';

const P = styled.p`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const WarningIcon = styled(Icon)`
  font-size: 2.5rem;
`;

const Warning = ({ children }) => {
  return (
    <Level margin="1rem 0">
      <Level.Item narrow>
        <WarningIcon icon="exclamation-triangle" color={colors.warning} />
      </Level.Item>
      <Level.Item>{children}</Level.Item>
    </Level>
  );
};

export default function SaveToXeroDialog({ invoice, resolve }) {
  const { workspace } = useWorkspace();

  const [{ isSubmitting, saved }, form] = useForm();
  const [xeroConnection, setXeroConnection] = useState({ isReady: false, data: null, error: null });
  const toast = useToast();
  const api = useApi();

  const validateAccountingIntegration = useCallback(async () => {
    if (!invoice) return;

    try {
      const { data } = await api.www.workspaces(workspace.id).invoices(invoice.id).accountingIntegration();

      setXeroConnection({ isReady: true, data: data.xeroConnection, error: null });
    } catch (error) {
      setXeroConnection({
        isReady: true,
        data: invoice.xeroConnection,
        error: error.message || 'Unable to validate accounting integration.',
      });
    }
  }, [api, workspace.id, invoice]);

  useEffect(() => {
    validateAccountingIntegration();
  }, [validateAccountingIntegration]);

  const handleSubmit = async () => {
    form.submit();

    try {
      const { data } = await api.www.workspaces(workspace.id).invoices(invoice.id).saveToXero();
      await resolve();
      toast.success(`Invoice has been saved to Xero with number #${data.invoice.number}.`);
    } catch (error) {
      toast.error(
        error.message ??
          'An error has occurred. The invoice has not been saved to Xero. Please review the Xero integration settings and try again.',
      );
      form.done();
    }
  };

  return (
    <ModalCard title="Save to Xero" onClose={() => resolve(false)}>
      <ModalCard.Body>
        <P>{`Are you sure you want to save this invoice to ${xeroConnection.data?.name || 'Xero'}?`}</P>
        {xeroConnection.error && <Warning>{xeroConnection.error}</Warning>}
      </ModalCard.Body>
      <ModalCard.Footer>
        <Buttons align="right">
          <CancelButton onClick={() => resolve(false)}>Cancel</CancelButton>
          <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSubmit}>
            Save
          </ActionButton>
        </Buttons>
      </ModalCard.Footer>
    </ModalCard>
  );
}
